import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isEmpty } from "lodash";
import { push } from "store/actions/router";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";
import ContributionSelector from "components/ContributionSelector";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import {
  getEmployerLinkRequests,
  getUserState,
  updateRequiredDisclosures,
} from "actions/userActions";
import { getAllAccounts } from "actions/accountActions";

import {
  accountConstants,
  contributionConstants,
  userConstants,
} from "actions/types";
import { scrollToTop } from "utils/dom";
import { getIraAccountIdSelector } from "store/selectors/user";
import {
  addPaycheckContributionSource,
  getContributionRecommendations,
} from "actions/contributionActions";
import { recurringContributionAccountIdSelector } from "store/selectors/accounts";
import type { RootState } from "types/state";

import "./SetupContribution.scss";

// Mirrors `recommendedContributionType` from statics/propTypes — the shape the
// recommendation amounts carry. `state.contribution.recommendations` is typed
// `object` in RootState, so the destructure below narrows to this shape.
interface RecommendedContribution {
  recommended: number;
  other: number[];
  isMaxContributionOption?: boolean;
}

interface ContributionRecommendations {
  scheduleFrequency: string;
  daily: RecommendedContribution;
  perPayPeriod: RecommendedContribution;
}

// Mirrors `ContributionAccount` from components/ContributionSelector — the
// account shape that component consumes. `state.accounts.iraAccounts` carries
// the broader `IraAccount` (id/accountType optional + nullable), so the
// accounts prop is cast to this narrower shape at the JSX boundary.
interface ContributionSelectorAccount {
  id: string;
  accountType: string;
}

const payPeriodToEnglish: Record<string, string> = {
  WEEKLY: "weekly",
  EVERY_OTHER_WEEK: "bi-weekly",
  TWICE_PER_MONTH: "semi-monthly",
  MONTHLY: "monthly",
};

const actions = [
  userConstants.USER_EMPLOYER_LINK_REQUESTS,
  accountConstants.GET_ACCOUNTS,
  userConstants.GET_INVESTMENT_PROFILE,
  contributionConstants.ONBOARDING_PAYCHECK_CONTRIBUTION,
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS,
  userConstants.USER_STATE,
];

const isSubmitting = createLoadingSelector([
  contributionConstants.ONBOARDING_PAYCHECK_CONTRIBUTION,
  userConstants.USER_STATE,
]);

const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  const { dailyMax, perPayPeriodMax } = state.contribution;
  const recommendations = state.contribution
    .recommendations as ContributionRecommendations;
  const { scheduleFrequency } = recommendations;
  return {
    error: errorSelector(state),
    groupId: get(state.user.employerLinkRequests, "0.group.id", null) as
      | string
      | null,
    dailyMax,
    perPayPeriodMax,
    dailyRecommendations: recommendations.daily,
    perPayPeriodRecommendations: recommendations.perPayPeriod,
    scheduleFrequency,
    hasLinkRequest: !isEmpty(state.user.employerLinkRequests),
    accountId: getIraAccountIdSelector(state),
    isSubmitting: isSubmitting(state),
    userState: get(state, "user.userState.state", "") as string,
    accounts: state.accounts.iraAccounts,
    recurringContributionAccountId:
      recurringContributionAccountIdSelector(state),
  };
};
const mapDispatchToProps = {
  push,
  getEmployerLinkRequests,
  getContributionRecommendations,
  getUserState,
  addPaycheckContributionSource,
  updateRequiredDisclosures,
  getAllAccounts,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  initialFetching: boolean;
}

class SetupContribution extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
    };
  }

  async componentDidMount() {
    window.analytics.page("Setup Contribution");
    scrollToTop();
    await this.props.getEmployerLinkRequests(
      this.props.client as ApolloClient<object>
    );

    await this.props.getContributionRecommendations(
      this.props.client as ApolloClient<object>,
      {
        groupId: this.props.groupId,
        isSelfEmployed: false,
      }
    );

    await this.props.getAllAccounts(this.props.client as ApolloClient<object>);

    this.setState({ initialFetching: false });
  }

  _submitPayCheckContribution = async (
    amount: number,
    isDailyCustom: boolean,
    accountId: string
  ) => {
    try {
      await this.props.addPaycheckContributionSource(
        this.props.client as ApolloClient<object>,
        {
          isDailyCustom,
          groupId: this.props.groupId as string,
          amount: +amount,
          accountId,
        }
      );
      window.analytics.track("Submitted Contribution");

      await this.props.getUserState(this.props.client as ApolloClient<object>);
      this.props.push("/dashboard");
    } catch (error) {
      return error;
    }
  };

  render() {
    const noErrorNotLoading = !this.props.error && !this.state.initialFetching;

    return (
      <>
        <div id="setup-contributions">
          <div className="mega-container">
            <div className="main-content">
              {this.state.initialFetching && <IconSpinner centered />}
              {this.props.error && (
                <Alert type="error" msg={this.props.error} />
              )}
              {noErrorNotLoading &&
                !isEmpty(this.props.perPayPeriodRecommendations) && (
                  <ContributionSelector
                    dailyMax={this.props.dailyMax as number | undefined}
                    perPayPeriodMax={
                      this.props.perPayPeriodMax as number | undefined
                    }
                    dailyRecommendations={this.props.dailyRecommendations}
                    perPayPeriodRecommendations={
                      this.props.perPayPeriodRecommendations
                    }
                    error={this.props.error}
                    onSubmit={this._submitPayCheckContribution}
                    isSubmitting={this.props.isSubmitting}
                    schedule={{ frequency: this.props.scheduleFrequency }}
                    isPaycheck={true}
                    showAccountSelector={true}
                    accounts={
                      this.props.accounts as ContributionSelectorAccount[]
                    }
                    defaultAccountId={
                      this.props.recurringContributionAccountId as
                        | string
                        | undefined
                    }
                  >
                    <section className="page-title-wrap">
                      <article className="text-cell">
                        <h1 className="page-title">
                          Your employer changed the pay periods for your plan.
                          Please choose a new{" "}
                          {payPeriodToEnglish[this.props.scheduleFrequency]}{" "}
                          contribution amount.
                        </h1>
                        <p className="page-subtext">
                          Please select the amount that{"'"}s good for you or
                          enter in a custom amount. You can modify your
                          contribution amount at any time.
                        </p>
                      </article>
                    </section>
                  </ContributionSelector>
                )}
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default connector(withApollo<PropsFromRedux>(SetupContribution));
